import { Card, CardContent, Divider, Stack, Tag, Typography } from '@optimus-web/core';
import { DateTimeService, lt, useAppClockService, useTimezone } from '@mews/framework';
import { AssistantSummaryCustomerBill } from '@mews-commander/data-access';
import { List } from '@optimus-web/core/src/List';
import { UnicodeEntity } from '@mews/enums';
import { useResolveRoute } from '@mews/routing';
import { RouteName } from '@mews-commander/enums';
import { localizeCurrencyValue, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { useLocalizedDate } from '../../drawer/hooks/useLocalizedDate';
import { navigateInBaseModal } from '../../../app/actions/common';
import { useCustomerOpenBillsQuery } from '../../query/useCustomerOpenBillsQuery';
import { StyledDivider, StyledListItem } from '../styles';

export interface CustomerBillsProps {
    customerId: string;
    enterpriseIds: Array<string>;
}

export const CustomerBills = ({ customerId, enterpriseIds }: CustomerBillsProps) => {
    const { data: result } = useCustomerOpenBillsQuery([customerId], enterpriseIds);
    const bills = result?.customerBills || [];

    if (bills?.length < 1) {
        return null;
    }

    return (
        <Card padding="0" overflowHidden>
            <CardContent padding="200">
                <Stack vertical spacing="150">
                    <Stack spacing="100">
                        <Typography textStyle="bodyMediumStrong" variant="primary" as="div">
                            {lt('BillPlural')}
                        </Typography>
                        <Typography textStyle="bodyMedium" variant="secondary" as="div">
                            {bills.length}
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
            <CardContent padding="0">
                <Divider />
            </CardContent>
            <CardContent padding="0">
                <List>
                    {bills.map((bill, index) => (
                        <Stack vertical key={bill.id}>
                            <Bill bill={bill} />
                            {index < bills.length - 1 && <StyledDivider borderStyle="dotted" />}
                        </Stack>
                    ))}
                </List>
            </CardContent>
        </Card>
    );
};

interface BillProps {
    bill: AssistantSummaryCustomerBill;
}

const Bill = ({ bill }: BillProps) => {
    const dueDateStr = useLocalizedDate(bill.dueDateUtc);
    const amountString = localizeCurrencyValue({ value: Number(bill.amount), currency: bill.currencyCode });
    const secondaryText = (
        <span>
            {dueDateStr}
            {UnicodeEntity.Space}
            {UnicodeEntity.ForwardSlash}
            {UnicodeEntity.Space}
            {amountString}
        </span>
    );

    const resolveRoute = useResolveRoute();
    const url = resolveRoute(RouteName.BillDetail, { id: bill.id });

    const dispatch = useCommanderThunkDispatch();
    const handleClick = () => dispatch(navigateInBaseModal(url));

    return (
        <StyledListItem
            primaryText={bill.name}
            secondaryText={secondaryText}
            rightItem={<RightItem dueDateUtc={bill.dueDateUtc} />}
            inactive
            onClick={handleClick}
        />
    );
};

interface RightItemProps {
    dueDateUtc: string;
}

const RightItem = ({ dueDateUtc }: RightItemProps) => {
    const timezone = useTimezone();
    const appClockService = useAppClockService();
    const now = useCommanderSelector((state) => getCurrentEnterpriseAppDateTimeNow(state, { appClockService }));
    const dueDate = DateTimeService.fromUtcString(dueDateUtc, timezone);

    const isOverdue = DateTimeService.isLess(dueDate, now);

    return <Tag variant={isOverdue ? 'warning' : 'primary'}>{isOverdue ? lt('Overdue') : lt('Unpaid')}</Tag>;
};
